import { useEffect, useState } from "react";
import { Check, Pencil, RefreshCw, Vote, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Button } from "@/components/ui/button";
import { ElectionEditor } from "@/components/election/ElectionEditor";
import { partyColour } from "@/lib/partyColours";


const Parties = ({ rows, testid }) => {
  const total = rows.reduce((a, r) => a + r.seats, 0) || 1;
  return (
    <div data-testid={testid}>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100">{rows.map((r, i) => <span key={r.party} style={{ width: `${(r.seats / total) * 100}%`, backgroundColor: partyColour(r.party, i) }} />)}</div>
      <ul className="mt-2 space-y-1 text-sm">{rows.map((r, i) => <li key={r.party} className="flex items-center gap-2"><span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: partyColour(r.party, i) }} /><span className="flex-1 truncate">{r.party}</span><b>{r.seats}</b></li>)}</ul>
    </div>
  );
};

const Box = ({ label, children, testid }) => (
  <div className="rounded-2xl border border-border bg-white p-4" data-testid={testid}><p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>{children}</div>
);

const Pending = ({ liveId, c, onDone }) => {
  const act = async (action) => { try { await api.post(`/admin/live/${liveId}/election/won`, { chamber: c.name, action }); toast.success(action === "confirm" ? "Result confirmed and now showing" : "Suggestion dismissed"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm sm:col-span-3" data-testid={`election-pending-${c.name}`}>
      <p className="font-medium text-amber-900">Admin: {c.won_pending.by === "seat table" ? "the approved seat table" : "the AI"} suggests this result. Members don't see it until you confirm.</p>
      <p className="mt-1 text-amber-900">{c.won_pending.parties.map((p) => `${p.party} ${p.seats}`).join(" · ")}</p>
      <div className="mt-2 flex gap-2">
        <Button size="sm" className="rounded-full" onClick={() => act("confirm")} data-testid={`election-confirm-${c.name}`}><Check size={14} className="mr-1" />Confirm result</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => act("dismiss")} data-testid={`election-dismiss-${c.name}`}><X size={14} className="mr-1" />Dismiss</Button>
      </div>
    </div>
  );
};

const blocColour = (b, i) => partyColour(b.parties?.[0]?.party || b.name, i);

const Blocs = ({ c }) => {
  const total = c.total_seats || c.blocs.reduce((a, b) => a + b.seats, 0) || 1;
  const major = c.blocs.slice(0, 2);
  return (
    <div className="rounded-2xl border border-border bg-[#FAF8F5] p-4 sm:col-span-3" data-testid={`election-blocs-${c.name}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">The two main blocs{c.majority ? ` · ${c.majority} seats needed for a majority` : ""}</p>
        <span className="text-[11px] text-slate-500" data-testid={`election-blocs-status-${c.name}`}>{c.count_status || "Latest reported count"}{c.blocs_at && ` · updated ${fmtDate(c.blocs_at)}`}</span>
      </div>
      <div className="relative mt-3 flex h-3 overflow-hidden rounded-full bg-slate-100">
        {c.blocs.map((b, i) => <span key={b.name} style={{ width: `${(b.seats / total) * 100}%`, backgroundColor: blocColour(b, i) }} />)}
        {c.majority && <span className="absolute inset-y-0 w-0.5 bg-slate-900" style={{ left: `${(c.majority / total) * 100}%` }} aria-label="Majority line" />}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {major.map((b, i) => (
          <div key={b.name} className="rounded-xl border border-border bg-white p-4" data-testid={`election-bloc-${i}`}>
            <p className="flex items-center gap-2 text-sm font-semibold"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: blocColour(b, i) }} />{b.name}</p>
            {b.leader && <p className="text-xs text-slate-500">Led by {b.leader}</p>}
            <p className="mt-2 font-serif text-4xl leading-none" data-testid={`election-bloc-seats-${i}`}>{b.seats}<span className="ml-1 text-sm text-slate-500">seats</span></p>
            {c.majority && <p className="mt-1 text-xs text-slate-500">{b.seats >= c.majority ? "Has a majority" : `${c.majority - b.seats} short of a majority`}</p>}
            {b.parties?.length > 0 && <ul className="mt-3 space-y-0.5 text-xs text-slate-600">{b.parties.map((p) => <li key={p.party} className="flex justify-between gap-2"><span className="truncate">{p.party}</span><b>{p.seats}</b></li>)}</ul>}
          </div>
        ))}
      </div>
      {c.blocs.slice(2).map((b) => <p key={b.name} className="mt-3 text-xs text-slate-600" data-testid="election-bloc-other"><b>{b.name}: {b.seats} seats</b>{b.parties?.length > 0 && ` (${b.parties.map((p) => `${p.party} ${p.seats}`).join(", ")})`}</p>)}
    </div>
  );
};

const Chamber = ({ c, liveId, admin, onDone }) => (
  <div className="grid gap-3 sm:grid-cols-3" data-testid={`election-chamber-${c.name}`}>
    {c.blocs?.length > 0 && <Blocs c={c} />}
    <Box label={`${c.name}: seats contested`} testid={`election-contested-${c.name}`}>
      <p className="font-serif text-5xl leading-none">{c.contested ?? "—"}</p>
      {c.total_seats != null && <p className="mt-2 text-xs text-slate-500">of {c.total_seats} seats{c.total_seats && c.contested === c.total_seats ? " (all of them)" : ""}</p>}
    </Box>
    <Box label="Contested seats currently held by" testid={`election-held-${c.name}`}>
      {c.held_by?.length ? <Parties rows={c.held_by} testid={`election-held-list-${c.name}`} /> : <p className="text-sm text-slate-500">Not available yet.</p>}
    </Box>
    <Box label="Who won" testid={`election-won-${c.name}`}>
      {c.won_by?.length ? <><Parties rows={c.won_by} testid={`election-won-list-${c.name}`} /><p className="mt-2 text-[11px] text-slate-500">Confirmed by Middle {fmtDate(c.won_confirmed_at)}</p></>
        : <p className="text-sm text-slate-500">{c.blocs?.length && c.majority ? `No bloc has reached ${c.majority} seats yet. A result shows here once a bloc has a majority in the final count.` : "Not confirmed yet. We'll show who won each seat once results are verified."}</p>}
    </Box>
    {admin && c.won_pending && <Pending liveId={liveId} c={c} onDone={onDone} />}
  </div>
);

export const ElectionBrief = ({ liveId }) => {
  const { me } = useMe();
  const admin = me?.role === "admin";
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const load = () => api.get(`/live/${liveId}/election`).then((r) => setB((d) => ({ ...r.data, since_last_visit: r.data.since_last_visit?.length ? r.data.since_last_visit : d?.since_last_visit }))).catch(() => setB({}));
  useEffect(() => { load(); }, [liveId]); // eslint-disable-line
  const refresh = async () => { setBusy(true); try { const { data } = await api.post(`/admin/live/${liveId}/election/refresh`); setB((d) => ({ ...data, since_last_visit: d?.since_last_visit })); toast.success("Updated from the latest verified Facts"); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  if (!b || (!b.overview && !b.chambers?.length && !admin)) return null;
  return (
    <section className="surface max-w-5xl space-y-5 p-5 sm:p-6" data-testid="election-brief">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex flex-1 items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--facts))]"><Vote size={15} />Election at a glance</p>
        <span className="text-[11px] text-slate-500" data-testid="election-brief-origin">{b.overview_by === "admin" ? "Reviewed by Middle" : "AI-drafted from verified Facts, updated every few hours"}{b.generated_at && ` · ${fmtDate(b.overview_by === "admin" ? b.edited_at || b.generated_at : b.generated_at)}`}</span>
        {admin && <><Button size="sm" variant="ghost" className="rounded-full" disabled={busy} onClick={refresh} data-testid="election-refresh-btn"><RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />Refresh with AI</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit(true)} data-testid="election-edit-btn"><Pencil size={14} className="mr-1" />Edit</Button></>}
      </div>
      {b.since_last_visit?.length > 0 && (
        <p className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid="election-since-last-visit">
          <b className="font-semibold">Since your last visit:</b> {b.since_last_visit.map((c) => `${c.bloc} ${c.from} → ${c.to}`).join(" · ")}
        </p>
      )}
      {b.overview ? <p className="font-serif text-lg leading-relaxed text-slate-800 sm:text-xl" data-testid="election-overview">{b.overview}</p>
        : <p className="text-sm text-slate-500">No summary yet. {admin && "Tap Refresh with AI to draft one."}</p>}
      {(b.chambers || []).map((c) => <Chamber key={c.name} c={c} liveId={liveId} admin={admin} onDone={load} />)}
      {admin && <ElectionEditor open={edit} onOpenChange={setEdit} liveId={liveId} brief={b} onSaved={(d) => { setB(d); setEdit(false); }} />}
    </section>
  );
};
